import { useCallback, useEffect, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { CheckCircle, Receipt, UserCircle } from "@phosphor-icons/react";
import { StatePill, money, qty, Spinner } from "./stockUtils";

export function ReorderTab({ options, refreshKey, onPoCreated, canEdit }) {
  const [data, setData] = useState(null);
  const [qtys, setQtys] = useState({});
  const [picked, setPicked] = useState({});
  const [deliverTo, setDeliverTo] = useState("");
  const [busy, setBusy] = useState("");
  const [opDlg, setOpDlg] = useState(null);
  const locs = options?.locations || [];
  const deliverToValue = deliverTo || (locs.find((l) => l.type === "store") || locs[0])?.id || "";

  const load = useCallback(async () => {
    try {
      const { data } = await API.get("/stock/reorder");
      setData(data);
      const q = {}, p = {};
      data.groups.forEach((g) => g.items.forEach((i) => { q[i.id] = i.suggested_qty || i.reorder_qty || 1; p[i.id] = true; }));
      setQtys(q); setPicked(p);
    } catch { setData({ groups: [], count: 0 }); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const lines = (g) => g.items.filter((i) => picked[i.id] && Number(qtys[i.id]) > 0).map((i) => ({ part_id: i.id, qty: Number(qtys[i.id]), unit_cost: i.unit_cost }));
  const raisePo = async (g) => {
    const ls = lines(g);
    if (!ls.length) return toast.error("Tick at least one part");
    setBusy(g.supplier_id || "none");
    try {
      const { data: po } = await API.post("/stock/pos", { supplier_id: g.supplier_id, deliver_to_location_id: deliverToValue, lines: ls });
      toast.success(`${po.po_number} drafted${g.supplier_id ? ` for ${g.supplier_name}` : ""} — review and send it from Purchase orders`);
      onPoCreated?.(po);
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't raise the purchase order"); } finally { setBusy(""); }
  };

  if (!data) return <Spinner />;
  return (
    <div className="space-y-4" data-testid="stock-reorder-tab">
      {data.count === 0 ? (
        <div className="text-center py-14 border border-dashed border-border rounded-lg" data-testid="reorder-empty">
          <CheckCircle size={36} className="mx-auto text-emerald-500 mb-2" weight="duotone" />
          <p className="font-semibold text-sm">Nothing to reorder</p>
          <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">Every part with a reorder point is above it. Parts drop in here (and admins get an alert) the moment stock falls to or below the reorder point.</p>
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground"><span className="font-semibold text-foreground" data-testid="reorder-count">{data.count}</span> part{data.count === 1 ? "" : "s"} at or below reorder point, grouped by preferred supplier. Tick what you want, adjust quantities, then raise a parts PO.</p>
            <div className="flex items-center gap-2"><Label className="text-xs text-muted-foreground">Deliver to</Label>
              {locs.length === 0 ? <span className="text-xs text-muted-foreground">No locations yet</span> : (
              <Select value={deliverToValue} onValueChange={setDeliverTo}><SelectTrigger className="w-[190px] h-8" data-testid="reorder-deliver-to"><SelectValue placeholder="Location" /></SelectTrigger>
                <SelectContent>{locs.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}</SelectContent></Select>)}</div>
          </div>
          {data.groups.map((g) => {
            const ls = lines(g);
            const est = ls.reduce((s, l) => s + l.qty * l.unit_cost, 0);
            return (
              <div key={g.supplier_id || "none"} className="border border-border rounded-lg bg-card overflow-hidden" data-testid={`reorder-group-${g.supplier_id || "none"}`}>
                <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-border bg-muted/30">
                  <div><p className="font-semibold text-sm">{g.supplier_name}</p><p className="text-[11px] text-muted-foreground">{g.items.length} part{g.items.length === 1 ? "" : "s"} · est. {money(est)}{!g.supplier_id && " · set a preferred supplier on these parts to route the PO"}</p></div>
                  {canEdit && <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setOpDlg({ group: g, lines: ls })} disabled={!ls.length} data-testid={`reorder-operative-${g.supplier_id || "none"}`}><UserCircle size={14} className="mr-1" /> Operative buys</Button>
                    <Button size="sm" onClick={() => raisePo(g)} disabled={!ls.length || busy === (g.supplier_id || "none")} data-testid={`reorder-raise-po-${g.supplier_id || "none"}`}><Receipt size={14} className="mr-1" /> {busy === (g.supplier_id || "none") ? "Raising…" : "Raise PO"}</Button>
                  </div>}
                </div>
                <table className="w-full text-sm">
                  <thead><tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border"><th className="px-4 py-2 w-8" /><th className="text-left px-2 py-2 font-semibold">Part</th><th className="text-left px-2 py-2 font-semibold">State</th><th className="text-right px-2 py-2 font-semibold">In stock</th><th className="text-right px-2 py-2 font-semibold">Reorder at</th><th className="text-right px-2 py-2 font-semibold">Order qty</th><th className="text-right px-4 py-2 font-semibold">Unit cost</th></tr></thead>
                  <tbody className="divide-y divide-border">
                    {g.items.map((i) => (
                      <tr key={i.id} className={picked[i.id] ? "" : "opacity-50"} data-testid={`reorder-row-${i.id}`}>
                        <td className="px-4 py-2"><input type="checkbox" checked={!!picked[i.id]} onChange={(e) => setPicked((p) => ({ ...p, [i.id]: e.target.checked }))} data-testid={`reorder-pick-${i.id}`} /></td>
                        <td className="px-2 py-2"><p className="font-medium">{i.name}</p><p className="text-[10px] text-muted-foreground">{[i.part_number, i.category].filter(Boolean).join(" · ")}</p></td>
                        <td className="px-2 py-2"><StatePill state={i.stock_state} /></td>
                        <td className="px-2 py-2 text-right">{qty(i.qty_total)} {i.unit}</td>
                        <td className="px-2 py-2 text-right text-muted-foreground">{qty(i.min_level)}</td>
                        <td className="px-2 py-2 text-right"><Input type="number" min="0" step="any" value={qtys[i.id] ?? ""} onChange={(e) => setQtys((q) => ({ ...q, [i.id]: e.target.value }))} className="h-8 w-24 ml-auto text-right" data-testid={`reorder-qty-${i.id}`} /></td>
                        <td className="px-4 py-2 text-right font-mono text-xs">{i.unit_cost > 0 ? money(i.unit_cost) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </>
      )}
      <OperativeBuyDialog state={opDlg} operatives={options?.operatives || []} onClose={() => setOpDlg(null)} onDone={load} />
    </div>
  );
}

function OperativeBuyDialog({ state, operatives, onClose, onDone }) {
  const [op, setOp] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setOp(""); }, [state]);
  if (!state) return null;
  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await API.post("/stock/reorder/to-operative-po", { operative_id: op, lines: state.lines });
      toast.success(`${data.added} line${data.added === 1 ? "" : "s"} added to ${data.po_number} — the operative buys and is reimbursed against it`);
      onDone(); onClose();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail) || "Couldn't add to the operative PO"); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="operative-buy-dialog">
        <DialogHeader><DialogTitle>Operative buys the parts</DialogTitle><DialogDescription>Adds {state.lines.length} part line{state.lines.length === 1 ? "" : "s"} to the operative's open in-house purchase order (creating a draft if needed) so they can buy locally and be reimbursed.</DialogDescription></DialogHeader>
        <Select value={op} onValueChange={setOp}><SelectTrigger data-testid="operative-buy-select"><SelectValue placeholder="Choose operative" /></SelectTrigger>
          <SelectContent>{operatives.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent></Select>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={submit} disabled={!op || saving} data-testid="operative-buy-confirm">{saving ? "Adding…" : "Add to their PO"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
